import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Badge, Button, DataTable, Modal, PageHeader, SearchFilterBar,
  type ColumnDef, type FilterDef, type SortState,
} from '@shared-ui/components';
import { pageLinkOf } from '@padcanvas/shared';
import { publishedLinksApi, type PublishedLink } from '../api/publishedLinksApi';
import { LinkForm } from './PublishedLinkForm';
import DeadLinks from './DeadLinks';
import {
  destinationLabel, destinationTarget, fmtLoad, loadRate, publicUrl, publisherLabel, shortUrl,
} from './publishedLinkFormat';
import './PublishedLinks.css';

/**
 * Published links — every URL we put in front of people, and what each one is
 * doing. See docs/FeatureDesigns/acquisition/published-links-target.md §9.
 *
 * The slug is fixed once created. Repointing changes where a link goes and
 * leaves the URL alone, which is the whole reason a link is a document: it is
 * already in video descriptions we cannot edit.
 *
 * The list is for finding a link; a row opens it (`PublishedLinkDetail`), which
 * is where it is edited, retired and printed as a QR.
 */

export default function PublishedLinks() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({ queryKey: ['publishedLinks'], queryFn: publishedLinksApi.list });
  const [filters, setFilters] = useState<Record<string, string>>({ status: 'active' });
  const [sort, setSort] = useState<SortState>({ sortBy: 'opens', sortOrder: 'desc' });
  const [creating, setCreating] = useState(false);

  const all = useMemo(() => data?.links ?? [], [data]);
  const publishers = useMemo(() => [...new Set(all.map(publisherLabel))].sort(), [all]);
  const links = all
    .filter(l => !filters.publisher || publisherLabel(l) === filters.publisher)
    .filter(l => !filters.status || l.status === filters.status);

  // A new link opens on its own page, where its QR is.
  const create = useMutation({
    mutationFn: (link: Partial<PublishedLink>) => publishedLinksApi.create(link),
    onSuccess: link => {
      void queryClient.invalidateQueries({ queryKey: ['publishedLinks'] });
      navigate(`/published-links/${link.slug}`);
    },
  });

  const filterDefs: FilterDef[] = [
    {
      key: 'publisher', kind: 'select', placeholder: 'All publishers',
      options: publishers.map(p => ({ value: p, label: p })),
    },
    {
      key: 'status', kind: 'select', placeholder: 'All statuses',
      options: [{ value: 'active', label: 'Active' }, { value: 'retired', label: 'Retired' }],
    },
  ];

  const columns: ColumnDef<PublishedLink>[] = [
    {
      key: 'url', header: 'URL', sortable: true, mobile: 'title', hideable: false,
      sortValue: l => l.slug,
      render: l => (
        <>
          {/* Opens the live URL, not the row. */}
          <a href={publicUrl(l)} target="_blank" rel="noreferrer" className="pl-slug"
            onClick={e => e.stopPropagation()}>
            {shortUrl(publicUrl(l))}
          </a>
          {/* The path form is a second spelling; the /p/ one still works. */}
          {pageLinkOf(l) && <div className="pl-sub">also /p/{l.slug}</div>}
        </>
      ),
    },
    {
      key: 'destination', header: 'Points at', sortable: true,
      sortValue: l => `${destinationLabel(l.destination)} ${l.destinationTitle ?? ''}`.toLowerCase(),
      render: l => (
        <>
          <div>{destinationLabel(l.destination)}</div>
          <div className="pl-sub">{l.destinationTitle ?? destinationTarget(l.destination)}</div>
        </>
      ),
    },
    {
      key: 'publisher', header: 'Publisher', sortable: true,
      sortValue: l => publisherLabel(l).toLowerCase(),
      render: publisherLabel,
    },
    {
      key: 'campaign', header: 'Campaign', sortable: true,
      sortValue: l => `${l.campaign ?? '￿'} ${l.variant ?? ''}`.toLowerCase(),
      render: l => (l.campaign
        ? <>{l.campaign}{l.variant && <span className="pl-sub"> · {l.variant}</span>}</>
        : '—'),
    },
    {
      key: 'opens', header: 'Opens', sortable: true, className: 'pl-num',
      sortValue: l => l.opens ?? 0,
      render: l => (l.opens ?? 0).toLocaleString(),
    },
    {
      // Opens whose page reported its first paint. The share of opens is the
      // part that matters: the rest are mostly followers who left on a blank
      // screen.
      key: 'loads', header: 'Loads', sortable: true, className: 'pl-num',
      sortValue: l => l.loads ?? 0,
      render: l => (
        <>
          <div>{(l.loads ?? 0).toLocaleString()}</div>
          <div className="pl-sub">{loadRate(l)} of opens</div>
        </>
      ),
    },
    // Tap → first paint: what half, seven in ten and nine in ten followers
    // waited at most. P90 is the tail an in-app browser on mobile data lives in.
    {
      key: 'p50Load', header: 'P50 load', sortable: true, className: 'pl-num',
      sortValue: l => l.p50LoadMs ?? -1,
      render: l => fmtLoad(l.p50LoadMs),
    },
    {
      key: 'p70Load', header: 'P70 load', sortable: true, className: 'pl-num',
      sortValue: l => l.p70LoadMs ?? -1,
      render: l => fmtLoad(l.p70LoadMs),
    },
    {
      key: 'p90Load', header: 'P90 load', sortable: true, className: 'pl-num',
      sortValue: l => l.p90LoadMs ?? -1,
      render: l => fmtLoad(l.p90LoadMs),
    },
    {
      key: 'status', header: 'Status', sortable: true, mobile: 'accessory',
      sortValue: l => l.status,
      render: l => (
        <Badge variant={l.status === 'active' ? 'success' : 'default'} size="sm">{l.status}</Badge>
      ),
    },
  ];

  return (
    <div className="pl">
      <PageHeader
        title="Published Links"
        subtitle={
          <>
            Every URL we publish — a partner’s chapter link, a campaign link, a QR code. Opens and
            loads are the last {data?.opensWindowDays ?? 30} days; a load time is the follower’s wait
            from tapping the link to the page’s first paint, measured on their own phone.
          </>
        }
        actions={<Button variant="primary" size="sm" onClick={() => { create.reset(); setCreating(true); }}>+ New Link</Button>}
      />

      <SearchFilterBar filters={filterDefs} values={filters} onChange={setFilters} hideSearch hideFetch />

      {error
        ? <p className="pl-error">Error: {(error as Error).message}</p>
        : (
          <DataTable<PublishedLink>
            columns={columns}
            data={links}
            rowKey={l => l.slug}
            onRowClick={l => navigate(`/published-links/${l.slug}`)}
            rowHref={l => `/published-links/${l.slug}`}
            rowClassName={l => (l.status === 'retired' ? 'pl-retired-row' : '')}
            sort={sort}
            onSortChange={setSort}
            loading={isLoading}
            mobileCards
            emptyMessage="No links match. Create one with + New Link."
          />
        )}

      <DeadLinks />

      <Modal isOpen={creating} onClose={() => setCreating(false)} title="New published link" size="md">
        {creating && (
          <LinkForm
            link={null}
            saving={create.isPending}
            error={create.error as Error | null}
            onCancel={() => setCreating(false)}
            onCreate={link => create.mutate(link)}
            onSave={() => undefined}
          />
        )}
      </Modal>
    </div>
  );
}

